/**
 * The "My Reports" catalogue — the report items a supervisor submits alongside
 * clocked time (PTO, holiday, bonuses, commission, reimbursements, driver logs).
 *
 * This module is the SINGLE source of truth for what reports exist and who may
 * see them. The API filters with {@link accessibleReports} and returns only the
 * survivors; the web app renders whatever it is given and never evaluates access
 * itself. That separation is deliberate — it is the seam that lets per-user
 * report grants replace the role rule later without touching the UI.
 *
 * Two entry SHAPES, which is what the table morphs between:
 *   - `hours`  — quantities of time (PTO, Holiday, Driver Logs) that become NetSuite
 *                TimeBills;
 *   - `amount` — money (Bonus, Commission, Reimbursement) that is injected onto a
 *                paycheck.
 *
 * DEDUCTIONS ARE DELIBERATELY ABSENT: NetSuite handles them itself (Michael,
 * 2026-08-25), so WorkLogix neither collects nor posts them.
 * The NetSuite payroll item behind each report is resolved per employee at
 * submit time, so a report is one concept here even though NetSuite carries a
 * per-department variant of many of them.
 */
import { Role } from './roles.js';
/** Every report id, in display order. */
export const REPORT_IDS = [
    'pto',
    'holiday',
    'bonus',
    'commission',
    'reimbursement',
    'driverLog',
];
/**
 * The catalogue. Every entry currently requires Manager (an Approver) — these
 * are supervisor-entered figures, and a View-Only user must never see bonus,
 * commission or deduction amounts. Admin sees everything.
 *
 * NOTE: the app roles cannot yet express "only the sales manager sees
 * Commission". When that is needed, replace the filter in
 * {@link accessibleReports} with a per-user grant lookup — the wire shape and
 * the UI stay exactly as they are.
 */
export const REPORT_DEFINITIONS = Object.freeze([
    {
        id: 'pto',
        label: 'PTO',
        shape: 'hours',
        description: 'Paid time off, entered in hours. Posts to NetSuite as time.',
        allowedRoles: [Role.Manager],
    },
    {
        id: 'holiday',
        label: 'Holiday',
        shape: 'hours',
        description: 'The paid company holiday calendar. Everyone with a holiday earning in NetSuite is paid ' +
            'from it. Supervisors can view it; only administrators can change it.',
        /**
         * READ for supervisors, WRITE for admins (Michael, 2026-09-13: "give
         * supervisors access to view the holiday table, but not edit it").
         *
         * This widens VISIBILITY only. The calendar decides what everyone is paid, so
         * the original rule (2026-08-19) kept supervisors out entirely; the reason to
         * relax it is that a supervisor approving time needs to know which days are
         * company holidays. Editing stays admin-only at the controller, where each
         * mutating route declares @Roles(Role.Admin) — the guard resolves
         * handler-before-class, so this line cannot grant a write.
         */
        allowedRoles: [Role.Manager],
    },
    {
        id: 'bonus',
        label: 'Bonus',
        shape: 'amount',
        description: 'One-off bonus amounts applied to a pay period.',
        allowedRoles: [Role.Manager],
    },
    {
        id: 'commission',
        label: 'Commission',
        shape: 'amount',
        description: 'Sales commission amounts applied to a pay period.',
        // ADMIN AND SALES ONLY (Michael, 2026-09-03): "Only admin and sales people should
        // see and manipulate that report."
        //
        // ⚠️ THIS NARROWS ACCESS. A Manager (Approver) could see Commission before today
        // and no longer can. Deliberate, and the reason report access had to become a set:
        // Sales is not a rung on the Admin > Manager > Employee ladder.
        allowedRoles: [Role.Sales],
    },
    {
        id: 'reimbursement',
        label: 'Reimbursements',
        shape: 'amount',
        description: 'Expense reimbursements and travel allowances.',
        allowedRoles: [Role.Manager],
    },
    {
        id: 'driverLog',
        label: 'Driver Logs',
        // HOURS, not amount (Michael, 2026-08-25): a Driver Log entry is person + date +
        // hours worked. Unlike PTO it is entered as a WEEKLY GRID over a persistent
        // roster of driver employees — submitting moves that week into history and
        // resets the grid — so it shares the 'hours' shape but not PTO's storage.
        shape: 'hours',
        description: 'Hours worked per driver per day, entered weekly.',
        allowedRoles: [Role.Manager],
    },
]);
/**
 * THE ACCESS PREDICATE. May a caller with these roles use this report?
 *
 * ⚠️ A SET, NOT A RANK. This replaced an "at least this role" comparison over a
 * privilege ladder (Admin 3 > Manager 2 > Employee 1). That model could not express
 * Commission being for Admin and SALES only: Sales is orthogonal to Manager, so any
 * rank that let Sales see Commission also let it see PTO, driver logs and the rest,
 * and any rank that did not, hid Commission from Sales entirely.
 *
 * An Administrator always passes — they hold the whole catalogue by definition. No
 * roles, or none that the report names, fails CLOSED.
 *
 * THE SINGLE SOURCE OF TRUTH for report access, used by both the catalogue and the
 * API. `accessibleReports` decides what a caller is OFFERED; this decides what they
 * may TOUCH, and the two must never disagree — hiding a report in a dropdown was
 * never the boundary.
 */
export function canAccessReport(reportId, roles) {
    if (roles.includes(Role.Admin)) {
        return true;
    }
    const def = REPORT_DEFINITIONS.find((d) => d.id === reportId);
    if (def === undefined) {
        return false;
    }
    return def.allowedRoles.some((allowed) => roles.includes(allowed));
}
/**
 * THE ACCESS SEAM. Given the caller's roles, return the reports they may see.
 *
 * Delegates to {@link canAccessReport} so the list a caller is offered cannot drift
 * from what the API will actually let them do.
 *
 * Replace the body of this function — not its signature — to move to per-user
 * grants; every caller (API + UI) goes through it.
 */
export function accessibleReports(roles) {
    return REPORT_DEFINITIONS.filter((def) => canAccessReport(def.id, roles)).map((def) => ({
        id: def.id,
        label: def.label,
        shape: def.shape,
        description: def.description,
    }));
}
/** Look up one definition by id (undefined when the id is unknown). */
export function findReport(id) {
    return REPORT_DEFINITIONS.find((def) => def.id === id);
}
//# sourceMappingURL=reports.js.map